import { useCallback, useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
   Booker,
   BookerStyleOptions,
   BookerVersions,
   ChangeRatesOnClickType,
   Destination,
   AddOnFormResponseType,
   NavBar,
   service,
   HotelFormRef,
   PopUpPromotionType,
} from 'booker'
import {
   bookerPlaceToHotelType,
   getDates,
   getForms,
   getSelectedDestination,
   getSelectedForm,
   getSiteUrl,
   isVertical,
} from '../utils/siteConfigUtils'
import qs from 'qs'
import { SiteConfig } from '../api/siteConfig'
import { HotelBookerRatesThunkRequest, getHotelBookerRatesThunk } from '../features/hotelBookerRatesSlice'
import { clear, getAddOnListThunk } from '../features/addOnListSlice'
import { clear as clearQuoteList, quoteAddOnListThunk } from '../features/quoteAddOnListSlice'
import { createTripThunk } from '../features/tripSlice'
import { decorateWithGaLinker, getExtraParamsFromQuery, getValidPromotionDates, getVisitorToken } from 'booker/utils'
import { Theme } from 'utils/layout'
import { clear as clearPopUpList, getPopUpListThunk } from '../features/popUpListSlice'
import PopUp from './PopUp'
import { reportUnknownGaCookieFormat } from '../utils/sentry'

const PlaygroundPanel = lazy(() => import('./PlaygroundPanel'))

export type HotelType = {
   id: number
   name: string
   place: {
      id: number
      name: string
   }
   destination: {
      id: number
      name: string
      airportCode: string
   }
   chain?: {
      id: number
      name: string
   }
   rooms?: Array<{
      id: number
      photos: Array<{
         basePath: string
         path: string
      }>
   }>
}

export type BookerWrapperTheme = Theme & BookerStyleOptions & { calendarColor?: string }

export type BookerWrapperForceOptions = {
   inline?: boolean | null
   showArrows?: boolean | null
   showLabels?: boolean | null
   showDatesFirst?: boolean | null
   servicesEmbeded?: boolean | null
   popupId?: number | null
   forceChatbot?: boolean
   playground?: boolean
}

type Props = {
   siteConfig: SiteConfig
   dataOnlyFormToShow: 'HOTEL_FLIGHT' | 'HOTEL' | 'SERVICES' | null
   hotelId?: number
   hotelList?: Array<HotelType>
   forceDestinationIdOnUrl?: boolean
   popupId?: number
   searchParams: URLSearchParams
   version?: BookerVersions
   theme?: BookerWrapperTheme
   forceOptions?: BookerWrapperForceOptions
   isEmbedded?: boolean
}

type quoteCalendarRatesProps = {
   range?: { startDate?: string; endDate?: string }
   adults: number
   children: number
   destination: Destination
}

// reemplaza el `_gl` que venga en los extras de la url (pudo haber expirado) por el de las cookies actuales
function navigateWithGaLinker(url: string) {
   window.location.href = decorateWithGaLinker(url, reportUnknownGaCookieFormat)
}

const BookerWrapper = ({
   siteConfig,
   dataOnlyFormToShow,
   hotelList,
   hotelId,
   forceDestinationIdOnUrl,
   searchParams,
   theme,
   version,
   forceOptions,
   isEmbedded,
}: Props) => {
   const completeHotelList = hotelList || bookerPlaceToHotelType(siteConfig.bookerPlaces, siteConfig.externalHotels)
   const forms = getForms(
      siteConfig,
      completeHotelList.map((c) => c.id),
      dataOnlyFormToShow,
      searchParams,
   )
   const { inline, showArrows, showLabels, showDatesFirst, servicesEmbeded } = forceOptions || {}
   const showStandaloneServices = dataOnlyFormToShow === 'SERVICES' ? true : siteConfig.showStandaloneServices
   const [selectedForm, setSelectedForm] = useState<service>(
      getSelectedForm({
         ...forms,
         showStandaloneServices,
      }),
   )
   const [promotionCode, setPromotionCode] = useState<string>()
   const dispatch = useAppDispatch()
   const hotelBookerRates = useAppSelector((state) => state.hotelBookerRates)
   const siteUrl = getSiteUrl(siteConfig)
   const addOnListWrapper = useAppSelector((state) => state.addOnListWrapper)
   const quoteAddOnListWrapper = useAppSelector((state) => state.quoteAddOnListWrapper)
   const popUpListWrapper = useAppSelector((state) => state.popUpListWrapper)
   const hotelFormRef = useRef<HotelFormRef>(null)
   const pendingPromoExtraParams = useRef<Record<string, string> | null>(null)
   const [tokenGeneration, setTokenGeneration] = useState(0)
   const visitorToken = getVisitorToken()

   const quoteHotelBookerRates = useCallback(
      ({ range, adults, children, destination }: quoteCalendarRatesProps) => {
         const request: HotelBookerRatesThunkRequest = {
            range,
            adults,
            children,
            destination,
         }
         dispatch(getHotelBookerRatesThunk(request))
      },
      [dispatch],
   )

   ///hotelId ? { destinationType: 'HOTEL', id: hotelId } : siteConfig.hotelForm?.destination,
   const initialDestination = useMemo(() => {
      if (siteConfig.bookerPlaces && siteConfig.bookerPlaces.length > 0) {
         return getSelectedDestination(siteConfig.bookerPlaces, siteConfig.hotelForm?.destination)
      }
   }, [siteConfig.bookerPlaces, siteConfig.hotelForm?.destination])

   const initialDates = useMemo(() => {
      return getDates({
         startDate: siteConfig.hotelForm?.startDate,
         endDate: siteConfig.hotelForm?.endDate,
      })
   }, [siteConfig.hotelForm?.startDate, siteConfig.hotelForm?.endDate])

   useEffect(() => {
      if (initialDestination && siteConfig.hotelForm?.quoteRates) {
         quoteHotelBookerRates({
            range: initialDates,
            adults: 2,
            children: 0,
            destination: initialDestination,
         })
      }
   }, [initialDates, initialDestination, quoteHotelBookerRates, siteConfig.hotelForm?.quoteRates])

   useEffect(() => {
      if (forceOptions?.playground) {
         // en modo playground la lista se siembra a mano con el botón "Comenzar" del panel de debug
         return
      }
      dispatch(getPopUpListThunk({ popupId: forceOptions?.popupId }))
      return () => {
         dispatch(clearPopUpList())
      }
   }, [dispatch, forceOptions?.popupId, forceOptions?.playground])

   function onHotelFlightSubmit(data: any) {
      const result = {
         departureAirportCode: data.origin.code,
         ...(data.destination.destinationType === 'destination'
            ? {
                 destinationId: data.destination.id,
                 arrivalAirportCode: data.destination.airportCode,
              }
            : {
                 hotelId: data.destination.id,
                 arrivalAirportCode: data.destination.airportCode,
              }),
         startDate: data.tripDates.startDate,
         endDate: data.tripDates.endDate,
         rooms: data.rooms,
         ...(data.promotionCode && { pc: data.promotionCode }),
         rvt: visitorToken,
      }
      let extras: Record<string, any> | null = null
      try {
         if (window.location.search) {
            const params = qs.parse(window.location.search, { ignoreQueryPrefix: true })
            extras = getExtraParamsFromQuery(params)
         }
      } catch (e) {}
      const query = qs.stringify(
         { ...result, ...extras },
         { arrayFormat: 'indices', allowDots: true, encodeValuesOnly: true },
      )
      if (data.destination.destinationType === 'destination') {
         navigateWithGaLinker(`${siteUrl}/hotel-flight/hotel?${query}`)
      } else if (data.destination.destinationType === 'hotel') {
         navigateWithGaLinker(`${siteUrl}/hotel-flight/room?${query}`)
      }
   }

   const onChangeRates = (data: ChangeRatesOnClickType) => {
      quoteHotelBookerRates(data)
   }

   function onHotelSubmit(data: any, submitUrl?: string) {
      if (submitUrl) {
         navigateWithGaLinker(submitUrl)
         return
      }
      const result = {
         checkIn: data.tripDates.startDate,
         checkOut: data.tripDates.endDate,
         rooms: data.rooms,
         ...(data.promotionCode && { pc: data.promotionCode }),
         ...(data.flexible && { flexible: data.flexible }),
         rvt: visitorToken,
      }
      let extras: Record<string, any> | null = null
      try {
         const params = qs.parse(window.location.search, { ignoreQueryPrefix: true })
         extras = getExtraParamsFromQuery(params)
      } catch (e) {}
      if (pendingPromoExtraParams.current) {
         extras = { ...(extras ?? {}), ...pendingPromoExtraParams.current }
         pendingPromoExtraParams.current = null
      }
      const query = qs.stringify(
         { ...result, ...extras },
         { arrayFormat: 'indices', allowDots: true, encodeValuesOnly: true },
      )
      if (data.destination?.destinationType === 'hotel') {
         // dispatch(setHotelQuoteListIsFetching(true))
         if (completeHotelList.length === 1 && !forceDestinationIdOnUrl) {
            navigateWithGaLinker(`${siteUrl}/rooms?${query}`)
         } else {
            navigateWithGaLinker(`${siteUrl}/${data.destination.id}/rooms?${query}`)
         }
      } else if (data.destination?.destinationType === 'destination') {
         navigateWithGaLinker(`${siteUrl}/hotels/${data.destination.id}?${query}`)
      }
   }

   async function onServicesSubmit(request: AddOnFormResponseType, callback: () => void) {
      const submitOnSameTab = servicesEmbeded ? false : siteConfig.submitOnSameTab
      const trip = await dispatch(createTripThunk(request)).unwrap()
      const query = qs.stringify({ t: trip.uuid, rvt: visitorToken })
      const url = decorateWithGaLinker(`${siteUrl}/trip-details?${query}`, reportUnknownGaCookieFormat)
      if (submitOnSameTab) {
         window.location.href = url
      } else {
         window.open(url, '_blank')
      }
      if (callback) {
         callback()
      }
   }

   function applyPromo(promoCode: string | null, promotion?: PopUpPromotionType, extraParams?: Record<string, string>) {
      if (!promoCode) {
         return
      }
      const promotionDates = promotion ? getValidPromotionDates(promotion) : null
      if (selectedForm === 'hotel') {
         pendingPromoExtraParams.current = extraParams ?? null
         hotelFormRef.current?.submit({
            promotionCode: promoCode,
            ...(promotionDates ? { tripDates: promotionDates } : {}),
         })
      } else if (selectedForm === 'hotelFlight') {
         // TODO apply promo code on hotel flight form
         setPromotionCode(promoCode)
      }
   }

   function drawNavBar() {
      const types = []
      if (forms.hotelForm) {
         types.push(<NavBar.HotelItem key="hotel-nav" />)
      }
      if (forms.hotelFlightForm) {
         types.push(<NavBar.HotelFlightItem key="hotel-flight-nav" />)
      }
      if (forms.serviceForm && showStandaloneServices) {
         types.push(<NavBar.ServiceItem key="services-nav" />)
      }
      return (
         (types.length > 1 || (siteConfig.externalServices && siteConfig.externalServices.length > 0)) && (
            <NavBar selected={selectedForm} onChange={setSelectedForm} useTailwindPrefix selectedClassName="active">
               {types}
               {siteConfig.externalServices?.map((service, index) => (
                  <NavBar.Item key={index} url={service.url} icon={service.icon}>
                     {service.displayText}
                  </NavBar.Item>
               ))}
            </NavBar>
         )
      )
   }

   function drawPopUps() {
      if (popUpListWrapper.isFetching || popUpListWrapper.isPristine) {
         return null
      }
      return popUpListWrapper.data?.map((popup) => (
         <PopUp
            key={`${popup.value.id}-${tokenGeneration}`}
            popUpWrapper={popup}
            onApplyPromo={applyPromo}
            locale={siteConfig.language}
            account={siteConfig.account.code}
            visitorToken={visitorToken}
         />
      ))
   }

   const calendarColor = theme ? theme.calendarColor || theme.primaryBookerBgColor || theme.primaryColor : undefined

   return (
      <>
         <Booker
            locale={siteConfig.language}
            dateFormat={siteConfig.dateFormat}
            hideIcon={siteConfig.hideCalendarIcon}
            isAutocomplete={false}
            vertical={isVertical()}
            originFetchUrl={process.env.REACT_APP_PUBLIC_AIRPORTS}
            useTailwindPrefix
            inline={!(inline === null || inline === undefined) ? inline : forms.hotelForm?.promoCodeInline}
            promoCodeInlineIcon={forms.hotelForm?.promoCodeInlineIcon}
            version={version || siteConfig.bookerVersion}
            buttonNoPadding={siteConfig.flags?.bookerButtonNoPadding}
            showArrows={
               !(showArrows === null || showArrows === undefined) ? showArrows : siteConfig.flags?.bookerShowArrows
            }
            showLabels={
               !(showLabels === null || showArrows === undefined) ? showLabels : siteConfig.flags?.bookerShowLabels
            }
            showDatesFirst={
               !(showDatesFirst === null || showDatesFirst === undefined)
                  ? showDatesFirst
                  : siteConfig.flags?.bookerShowDatesFirst
            }
            {...(theme && {
               ...(calendarColor && { primaryColor: calendarColor }),
               styleOptions: theme,
            })}
         >
            {drawNavBar()}
            {forms.hotelForm && selectedForm === 'hotel' && (
               <Booker.Hotel
                  ref={hotelFormRef}
                  hotelForm={forms.hotelForm}
                  hotelList={completeHotelList}
                  onSubmit={onHotelSubmit}
                  request={{
                     tripDates: initialDates,
                     destination: initialDestination,
                     promotionCode,
                  }}
                  rates={hotelBookerRates}
                  onChangeRates={onChangeRates}
               />
            )}
            {forms.hotelFlightForm && selectedForm === 'hotelFlight' && (
               <Booker.HotelFlight
                  hotelFlightForm={forms.hotelFlightForm}
                  hotelList={completeHotelList}
                  onSubmit={onHotelFlightSubmit}
                  request={{
                     tripDates: siteConfig.hotelFlightForm ? getDates(siteConfig.hotelFlightForm) : undefined,
                     destination: getSelectedDestination(siteConfig.bookerPlaces, forms.hotelFlightForm.destination),
                     promotionCode,
                  }}
               />
            )}
            {forms.serviceForm && selectedForm === 'service' && showStandaloneServices && (
               <Booker.Services
                  serviceForm={forms.serviceForm}
                  hotelList={[]}
                  onSubmit={onServicesSubmit}
                  getAddOnList={(request) => dispatch(getAddOnListThunk(request))}
                  clearAddOnList={() => dispatch(clear())}
                  addOnListWrapper={addOnListWrapper}
                  quoteListWrapper={quoteAddOnListWrapper}
                  clearQuoteList={() => dispatch(clearQuoteList())}
                  onQuote={(request) => dispatch(quoteAddOnListThunk(request))}
               />
            )}
         </Booker>
         {!isEmbedded && drawPopUps()}
         {forceOptions?.playground && (
            <Suspense fallback={null}>
               <PlaygroundPanel
                  account={siteConfig.account.code}
                  onVisitorTokenChanged={() => setTokenGeneration((prev) => prev + 1)}
               />
            </Suspense>
         )}
      </>
   )
}

export default BookerWrapper
